// src/components/rcv/SubirDocumento.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Upload,
  FileUpload,
  Close,
  Search,
  CheckCircle
} from '@mui/icons-material';
import { dtedocService } from '../../services/dtedocService';

const SubirDocumento = ({
  rutEmisor,
  tipoDoc,
  folio,
  movimientoId,
  rutEmpresa,
  onClose,
  onDocumentoCreado
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingSii, setLoadingSii] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [archivo, setArchivo] = useState(null);

  // Documento encontrado en SII esperando confirmación
  const [documentoEncontrado, setDocumentoEncontrado] = useState(null);

  // Vista previa PDF
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const pdfUrlRef = useRef(null);

  // Carga la vista previa cuando se encuentra un documento
  useEffect(() => {
    if (!documentoEncontrado) {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
      setPdfUrl(null);
      return;
    }
    let cancelled = false;
    setLoadingPdf(true);
    dtedocService.getDocumentoPdf(documentoEncontrado.id)
      .then(blob => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        pdfUrlRef.current = url;
        setPdfUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPdfUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingPdf(false);
      });
    return () => {
      cancelled = true;
    };
  }, [documentoEncontrado?.id]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['application/pdf', 'text/xml', 'application/xml'];
      const validExtensions = ['.pdf', '.xml'];
      let isValid = validTypes.includes(file.type);
      if (!isValid) {
        isValid = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      }
      if (!isValid) {
        setError("Solo se permiten archivos PDF o XML");
        return;
      }
      setArchivo(file);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!archivo) {
      setError("Por favor seleccione un archivo para subir");
      return;
    }
    if (!rutEmisor || !tipoDoc || !folio) {
      setError("Faltan datos del documento (RUT emisor, tipo, folio)");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', archivo);
      formData.append('rut_emisor', rutEmisor);
      formData.append('tipo_doc', tipoDoc);
      formData.append('folio', folio);
      if (movimientoId) {
        formData.append('movimiento_id', movimientoId);
      }

      const respuesta = await dtedocService.uploadDocumento(formData);

      if (movimientoId && respuesta.id) {
        await dtedocService.asociarDocumentoAMovimiento(respuesta.id, movimientoId);
      }

      setSuccess("Documento subido exitosamente");
      setArchivo(null);
      if (onDocumentoCreado) onDocumentoCreado(respuesta);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      console.error("Error al subir documento:", err);
      setError(err.message || "Error al subir el documento");
    } finally {
      setLoading(false);
    }
  };

  const handleBuscarEnSii = async () => {
    if (!movimientoId || !rutEmpresa) {
      setError('Faltan datos para buscar en SII (movimiento o empresa)');
      return;
    }
    setLoadingSii(true);
    setError(null);
    setSuccess(null);
    try {
      const respuesta = await dtedocService.buscarEnSii(movimientoId, rutEmpresa);
      setDocumentoEncontrado(respuesta);
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('404') || msg.toLowerCase().includes('no encontrado')) {
        setError('El documento no fue encontrado en el portal del SII. Verifique que exista o adjúntelo manualmente.');
      } else if (msg.includes('401') || msg.toLowerCase().includes('credencial')) {
        setError('Credenciales SII incorrectas. Verifique la configuración en la sección Empresa.');
      } else if (msg.includes('502')) {
        setError('Error de redirección en el portal SII. Intente más tarde.');
      } else if (msg) {
        setError(msg);
      } else {
        setError('Error al conectar con el portal SII. Intente más tarde.');
      }
    } finally {
      setLoadingSii(false);
    }
  };

  const handleConfirmarAdjunto = async () => {
    if (!documentoEncontrado) return;
    setLoadingConfirm(true);
    setError(null);
    try {
      await dtedocService.asociarDocumentoAMovimiento(documentoEncontrado.id, movimientoId);
      setSuccess('Documento adjuntado correctamente');
      if (onDocumentoCreado) onDocumentoCreado(documentoEncontrado);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(err.message || 'Error al adjuntar el documento');
    } finally {
      setLoadingConfirm(false);
    }
  };

  const handleDescartarDocumento = async () => {
    if (!documentoEncontrado) return;
    try {
      await dtedocService.deleteDocumento(documentoEncontrado.id);
    } catch (_) {
      // Si falla el delete, no importa — el archivo queda huérfano pero no asociado
    }
    setDocumentoEncontrado(null);
    setError(null);
  };

  const anyLoading = loading || loadingSii || loadingConfirm;

  // ── Vista: documento encontrado, esperando confirmación ──────────────────
  if (documentoEncontrado) {
    const kb = Math.round(documentoEncontrado.tamanio / 1024);
    return (
      <>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle color="success" />
              <Typography variant="h6">Documento encontrado en SII</Typography>
            </Box>
            <IconButton edge="end" onClick={onClose} disabled={anyLoading}>
              <Close />
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Tipo {documentoEncontrado.tipo_doc} · Folio {documentoEncontrado.folio} · Emisor {documentoEncontrado.rut_emisor} · {kb} KB
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, overflow: 'hidden', position: 'relative' }}>
          {loadingPdf && (
            <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }} />
          )}
          {pdfUrl ? (
            <iframe
              src={`${pdfUrl}#view=FitH`}
              title="Vista previa PDF"
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              {loadingPdf
                ? <CircularProgress />
                : <Typography variant="body2" color="text.secondary">No se pudo cargar la vista previa</Typography>
              }
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 2, pb: 2 }}>
          {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ width: '100%' }}>{success}</Alert>}
          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={handleDescartarDocumento} disabled={anyLoading} color="inherit">
              No, descartar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmarAdjunto}
              disabled={anyLoading}
              startIcon={loadingConfirm ? <CircularProgress size={20} /> : <CheckCircle />}
            >
              {loadingConfirm ? 'Adjuntando…' : 'Sí, agregar como adjunto'}
            </Button>
          </Box>
        </DialogActions>
      </>
    );
  }

  // ── Vista normal: buscar o subir ─────────────────────────────────────────
  return (
    <>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Adjuntar documento</Typography>
          <IconButton edge="end" onClick={onClose} disabled={anyLoading}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 3,
          minHeight: '50vh'
        }}>
          <Typography variant="h6" gutterBottom>
            No se encontró ningún documento asociado
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Puede obtenerlo directamente desde el SII o subir un archivo local.
          </Typography>

          <Box sx={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Opción 1: Buscar en SII */}
            <Tooltip title={!rutEmpresa ? 'No hay empresa configurada' : ''}>
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  sx={{ py: 1.5 }}
                  startIcon={loadingSii ? <CircularProgress size={20} color="inherit" /> : <Search />}
                  onClick={handleBuscarEnSii}
                  disabled={anyLoading || !rutEmpresa || !movimientoId}
                >
                  {loadingSii ? 'Buscando en SII… (esto puede tardar)' : 'Buscar en SII'}
                </Button>
              </span>
            </Tooltip>

            <Divider>o</Divider>

            {/* Opción 2: Subir archivo local */}
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ py: 1.5 }}
              startIcon={<FileUpload />}
              disabled={anyLoading}
            >
              {archivo ? archivo.name : 'Seleccionar archivo local…'}
              <input
                type="file"
                accept=".pdf,.xml"
                hidden
                onChange={handleFileChange}
              />
            </Button>

            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mt: 1 }}>
                {success}
              </Alert>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={anyLoading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!archivo || anyLoading}
          startIcon={loading ? <CircularProgress size={20} /> : <Upload />}
        >
          {loading ? 'Subiendo...' : 'Subir archivo'}
        </Button>
      </DialogActions>
    </>
  );
};

export default SubirDocumento;
